/*! Copyright (c) Safe As Milk. All rights reserved. */
class ProductInteractiveOptions extends HTMLElement {
    #boundOnClick;
    #boundOnChange;
    #listeners;
    constructor() {
        super();
        this.#boundOnClick = this.#onClick.bind(this);
        this.#boundOnChange = this.#onChange.bind(this);
    }
    connectedCallback() {
        this.variantsControlElement = this.parentElement;
        this.#listeners = [];
        this.#refreshState();
        this.removeEventListener("click", this.#boundOnClick);
        this.removeEventListener("change", this.#boundOnChange);
        this.addEventListener("click", this.#boundOnClick);
        this.addEventListener("change", this.#boundOnChange);
        this.#listeners.push({
            element: this,
            event: "click",
            handler: this.#boundOnClick
        }, {
            element: this,
            event: "change",
            handler: this.#boundOnChange
        });
        requestAnimationFrame((() => {
            this.#refreshState();
            if (this.dataset.variantId) {
                const id = Number(this.dataset.variantId);
                this.selectVariantById(id);
            } else if (this.currentVariant) {
                this.setOptionInputsAvailability();
            }
        }));
    }
    disconnectedCallback() {
        this.#listeners.forEach((listener => {
            listener.element.removeEventListener(listener.event, listener.handler);
        }));
        this.variantsControlElement.removeEventListener("change", this.variantsControlElement.onVariantChange);
    }
    getOptionsElementsObject() {
        const options = Array.from(this.variantsControlElement.querySelectorAll("input, select > option"));
        const obj = options.reduce(((finalObject, el) => {
            if (el.nodeName === "INPUT") {
                const optionName = el.name.trim();
                if (!finalObject.hasOwnProperty(optionName)) finalObject[optionName] = {
                    position: Object.keys(finalObject).length,
                    type: "inputs",
                    elements: {}
                };
                finalObject[optionName].elements[el.value] = {
                    element: el
                };
            } else if (el.nodeName === "OPTION") {
                const selectElement = el.parentElement;
                const optionName = selectElement.name.replace("options[", "").replace("]", "").trim();
                if (!finalObject.hasOwnProperty(optionName)) {
                    finalObject[optionName] = {
                        position: Object.keys(finalObject).length,
                        type: "select",
                        elements: {},
                        selectElement: selectElement
                    };
                }
                finalObject[optionName].elements[el.value] = {
                    element: el,
                    text: el.innerHTML.trim()
                };
            }
            return finalObject;
        }), {});
        return obj;
    }
    #refreshState() {
        if (!this.variantsControlElement) return;
        this.inputs = this.getOptionsElementsObject();
        this.options = Object.keys(this.inputs).reduce(((options, key) => {
            options[this.inputs[key].position] = key;
            return options;
        }), []);
        this.variants = this.variantsControlElement.getVariantData();
        this.currentVariant = this.getCurrentVariant();
        this.variantsControlElement.removeEventListener("change", this.variantsControlElement.onVariantChange);
    }
    getCurrentOptions() {
        return Array.from(this.variantsControlElement.querySelectorAll("input:checked, select")).map((input => input.value));
    }
    getCurrentVariant() {
        const options = this.getCurrentOptions();
        const result = this.variants.filter((variant => options.every(((option, index) => variant.options[index] === option))));
        return result[0] || null;
    }
    getVariantById(id) {
        const [result] = this.variants.filter((variant => variant.id === id));
        return result || null;
    }
    getMatchingVariantsForOptionValue(optionPosition, optionValue, selectedOptions = this.getCurrentOptions()) {
        return this.variants.filter((variant => {
            if (variant.options[optionPosition] !== optionValue) return false;
            return selectedOptions.every(((selectedOption, index) => {
                if (index === optionPosition || !selectedOption) return true;
                if (optionPosition === 0) return true;
                if (index > optionPosition) return true;
                return variant.options[index] === selectedOption;
            }));
        }));
    }
    optionsAvailableInOtherVariants() {
        const currentVariant = this.getCurrentVariant();
        if (!currentVariant || !currentVariant.options) return [];
        const availableVariants = this.variants.filter((variant => variant.available && variant.id !== currentVariant.id && variant.options[0] === currentVariant.options[0]));
        return currentVariant.options.reduce(((options, option, i) => {
            if (availableVariants.some((variant => variant.options.map(((o, j) => `${o}_${j}`)).includes(`${option}_${i}`)))) options.push(`${option}_${i}`);
            return options;
        }), []);
    }
    setOptionInputsAvailability() {
        this.currentVariant = this.getCurrentVariant();
        if (!this.inputs || !this.variants) return;
        const selectedOptions = this.getCurrentOptions();
        Object.keys(this.inputs).forEach((option => {
            Object.keys(this.inputs[option].elements).forEach((value => {
                const {element: element, text: text} = this.inputs[option].elements[value];
                const optionPosition = this.inputs[option].position;
                const matchingVariants = this.getMatchingVariantsForOptionValue(optionPosition, value, selectedOptions);
                const hasMatchingVariant = matchingVariants.length > 0;
                const hasAvailableVariant = matchingVariants.some((variant => variant.available));
                const optionItem = element.closest(".product-form__swatch__item");
                // Color (position 0) never gets disabled/strikethrough.
                // Size/Inseam (position > 0): hide when no matching variant exists,
                // strikethrough when matching variant exists but is sold out.
                if (optionItem) {
                    optionItem.hidden = !hasMatchingVariant;
                }
                if (optionPosition > 0 && this.inputs[option].type === "inputs") {
                    // Fallback: if no swatch item container found, hide the label/input wrapper
                    if (!optionItem) {
                        const label = element.nextElementSibling;
                        if (label) {
                            label.style.display = hasMatchingVariant ? '' : 'none';
                        }
                        // Also try hiding parent element
                        const parent = element.parentElement;
                        if (parent && parent.classList.contains('product-form__swatch__item')) {
                            parent.hidden = !hasMatchingVariant;
                        }
                    }
                    // Add disabled class for sold out variants that exist
                    if (element.nextElementSibling) {
                        element.nextElementSibling.classList.toggle("is-disabled", !hasAvailableVariant && hasMatchingVariant);
                    }
                }
                if (this.inputs[option].type === "select") {
                    element.hidden = !hasMatchingVariant;
                    element.innerHTML = text;
                    if (!hasAvailableVariant && hasMatchingVariant) {
                        element.innerHTML = `${text} - ${window.theme.localize("SOLD_OUT")}`;
                    }
                }
            }));
        }));
    }
    getFirstExistingVariant(option, optionGroup) {
        const optionPosition = this.options.indexOf(optionGroup);
        const selectedOptions = this.getCurrentOptions();
        const existingVariantsWithOption = this.variants.filter((variant => variant.options[optionPosition] === option && (optionPosition === 0 || variant.options.slice(0, optionPosition).every(((o, index) => o === selectedOptions[index])))));
        for (let i = 0, l = existingVariantsWithOption.length; i < l; i += 1) {
            if (existingVariantsWithOption[i].available) return existingVariantsWithOption[i];
        }
        return existingVariantsWithOption.length > 0 ? existingVariantsWithOption[0] : null;
    }
    getExistingVariantFromSelectedOption(option, optionGroup) {
        const options = this.getCurrentOptions();
        const optionPosition = this.options.indexOf(optionGroup);
        options[optionPosition] = option;
        const variant = this.variants.find((v => v.options.every(((val, idx) => val === options[idx]))));
        return variant || null;
    }
    selectVariant(variant) {
        if (!variant || !variant.options) return;
        const currentFocusedElement = document.activeElement;
        const optionsByGroup = variant.options.reduce(((grouped, option, index) => {
            grouped[this.options[index]] = option;
            return grouped;
        }), {});
        Object.keys(this.inputs).forEach((option => {
            Object.keys(this.inputs[option].elements).forEach((value => {
                const {element: element, text: text} = this.inputs[option].elements[value];
                element.disabled = false;
                if (this.inputs[option].type === "inputs") {
                    element.nextElementSibling.classList.remove("is-disabled");
                    element.checked = false;
                }
                if (this.inputs[option].type === "select") element.innerHTML = text;
                if (optionsByGroup[option] === value) {
                    if (this.inputs[option].type === "inputs") element.checked = true;
                    if (this.inputs[option].type === "select") this.inputs[option].selectElement.value = value;
                }
            }));
        }));
        this.setOptionInputsAvailability();
        if (currentFocusedElement) {
            currentFocusedElement.focus();
        }
    }
    selectVariantById(id) {
        const variant = this.getVariantById(id);
        if (variant) {
            this.selectVariant(variant);
            this.variantsControlElement.onVariantChange();
        }
    }
    selectCurrentOrFirstExistingVariant(option, optionGroup) {
        const selectedVariant = this.getExistingVariantFromSelectedOption(option.trim(), optionGroup.trim());
        if (selectedVariant) {
            this.selectVariant(selectedVariant);
            if (this.variantsControlElement && typeof this.variantsControlElement.onVariantChange === "function") this.variantsControlElement.onVariantChange();
        } else {
            const firstExistingVariant = this.getFirstExistingVariant(option.trim(), optionGroup.trim());
            if (firstExistingVariant) {
                this.selectVariant(firstExistingVariant);
                if (this.variantsControlElement && typeof this.variantsControlElement.onVariantChange === "function") this.variantsControlElement.onVariantChange();
            }
        }
    }
    #onClick(e) {
        const input = e.target.closest('input[type="radio"]');
        if (!input || !this.contains(input) || input.disabled) return;
    }
    #onChange(e) {
        const target = e.target.closest("input, select");
        if (!target || !this.contains(target) || target.disabled) return;
        this.#refreshState();
        if (target.tagName === "INPUT" && target.type !== "radio") return;
        const optionName = target.name.trim().replace("options[", "").replace("]", "");
        const optionValue = target.value.trim();
        this.selectCurrentOrFirstExistingVariant(optionValue, optionName);
    }
}

customElements.define("product-interactive-options", ProductInteractiveOptions);
